import axios from 'axios'
import { configService } from '@/services/configService'
import { Region as GlobalRegion, RegionFilters as GlobalRegionFilters } from '@/services/configService'

export interface Merchant {
  merchant_id: string
  business_name: string
  business_description: string
  business_type: string
  phone: string
  email: string
  website: string
  tax_id: string
  logo_url?: string | null
  is_active: boolean
  user_id: string
  created_at: string
  updated_at: string
  average_rating: number
  review_count: number
  // Corporate hierarchy fields
  organization_id?: string
  org_region_id?: string
  store_code?: string
  parent_store_id?: string
  organization_name?: string
  organization_active?: boolean
  org_region_name?: string
  org_region_code?: string
  parent_store_name?: string
  // Address fields
  address_id?: string
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  is_primary?: boolean
}

export interface CreateMerchantDto {
  business_name: string
  business_description: string
  business_type: string
  tax_id: string
  logo?: File | null
  is_active: boolean
  user_id: string
  merchant_owner_id?: string
  // Corporate hierarchy fields
  organization_id?: string
  org_region_id?: string
  store_code?: string
  parent_store_id?: string
  // Address fields
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  is_primary?: boolean
}

export interface UpdateMerchantDto {
  business_name?: string
  business_description?: string
  business_type?: string
  tax_id?: string
  logo?: File | null
  is_active?: boolean
  // Corporate hierarchy fields
  organization_id?: string
  org_region_id?: string
  store_code?: string
  parent_store_id?: string
  // Address fields
  address_line1?: string
  address_line2?: string
  city?: string
  state?: string
  postal_code?: string
  country?: string
  latitude?: number
  longitude?: number
  is_primary?: boolean
}

export interface PaginatedResponse<T> {
  merchants: T[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface MerchantUser {
  merchant_user_id: string
  merchant_id: string
  user_id: string
  permission_level: 'owner' | 'admin' | 'manager' | 'viewer'
  is_active: boolean
  created_at: string
  updated_at: string
  user_email?: string
  user_name?: string
  user_first_name?: string
  user_last_name?: string
  user_phone?: string
  can_manage_users?: boolean
  can_manage_products?: boolean
  can_manage_offers?: boolean
  can_view_reports?: boolean
  created_by?: string
  merchant_name?: string
}

export interface MerchantUserResponse {
  merchant_id: string
  users: MerchantUser[]
}

export interface AddMerchantUserDto {
  user_id: string
  permission_level?: 'owner' | 'admin' | 'manager' | 'viewer'
  is_active?: boolean
  can_manage_users?: boolean
  can_manage_products?: boolean
  can_manage_offers?: boolean
  can_view_reports?: boolean
}

export interface UpdateMerchantUserDto {
  permission_level?: 'owner' | 'admin' | 'manager' | 'viewer'
  is_active?: boolean
  can_manage_users?: boolean
  can_manage_products?: boolean
  can_manage_offers?: boolean
  can_view_reports?: boolean
}

export interface MerchantFilters {
  page?: number
  limit?: number
  business_type?: string
  is_active?: boolean
  search?: string
}

// Organization and Region interfaces for global brand selection
export interface Organization {
  organization_id: string
  name: string
  description?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface OrganizationRegion {
  region_id: string
  organization_id: string
  name: string
  code: string
  description?: string
  manager_user_id?: string
  geographic_region_id?: string
  is_active: boolean
  created_at: string
  updated_at: string
}

export interface CreateOrganizationRegionDto {
  name: string
  code: string
  description?: string
  manager_user_id?: string
  geographic_region_id?: string
  is_active?: boolean
}

export interface UpdateOrganizationRegionDto {
  name?: string
  code?: string
  description?: string
  manager_user_id?: string
  geographic_region_id?: string
  is_active?: boolean
}

export interface OrganizationsResponse {
  organizations: Organization[]
}

export interface OrganizationRegionsResponse {
  organization_id: string
  regions: OrganizationRegion[]
}

export interface CreateOrganizationDto {
  name: string
  description?: string
  is_active?: boolean
}

export interface UpdateOrganizationDto {
  name?: string
  description?: string
  is_active?: boolean
}

// Use proxy configuration from vite.config.ts
const API_BASE_URL = '/api'

const merchantApi = axios.create({
  baseURL: `${API_BASE_URL}/merchants`,
  headers: {
    'Content-Type': 'application/json',
  },
})

const organizationApi = axios.create({
  baseURL: `${API_BASE_URL}/organizations`,
  headers: {
    'Content-Type': 'application/json',
  },
})

// Add request interceptor for authentication
const addAuthInterceptor = (instance: any) => {
  instance.interceptors.request.use(
    (config: any) => {
      const token = localStorage.getItem('access_token')
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
      return config
    },
    (error: any) => {
      return Promise.reject(error)
    }
  )

  instance.interceptors.response.use(
    (response: any) => response,
    (error: any) => {
      if (error.response?.status === 401) {
        // Handle unauthorized access
        localStorage.removeItem('access_token')
        window.location.href = '/login'
      }
      return Promise.reject(error)
    }
  )
}

addAuthInterceptor(merchantApi)
addAuthInterceptor(organizationApi)

export const merchantService = {
  // Get all merchants with pagination and filtering
  async getAll(filters: MerchantFilters = {}): Promise<PaginatedResponse<Merchant>> {
    const params = new URLSearchParams()
    
    if (filters.page) params.append('page', filters.page.toString())
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.business_type) params.append('business_type', filters.business_type)
    if (filters.is_active !== undefined) params.append('is_active', filters.is_active.toString())
    if (filters.search) params.append('search', filters.search)

    const response = await merchantApi.get('', { params })
    return response.data
  },

  // Get merchant by ID
  async getById(id: string): Promise<Merchant> {
    const response = await merchantApi.get(`/${id}`)
    return response.data
  },

  // Create new merchant (multipart/form-data, supports optional logo file)
  async create(merchantData: CreateMerchantDto): Promise<Merchant> {
    const formData = new FormData()
    Object.entries(merchantData).forEach(([key, value]) => {
      if (key === 'logo' || value === undefined || value === null) return
      formData.append(key, String(value))
    })
    if (merchantData.logo) {
      formData.append('logo', merchantData.logo)
    }

    const response = await merchantApi.post('', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Update merchant (multipart/form-data, supports optional logo file)
  async update(id: string, merchantData: UpdateMerchantDto): Promise<Merchant> {
    const formData = new FormData()
    Object.entries(merchantData).forEach(([key, value]) => {
      if (key === 'logo' || value === undefined || value === null) return
      formData.append(key, String(value))
    })
    if (merchantData.logo) {
      formData.append('logo', merchantData.logo)
    }

    const response = await merchantApi.put(`/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  // Delete merchant (soft delete)
  async delete(id: string): Promise<{ message: string; merchant: Merchant }> {
    const response = await merchantApi.delete(`/${id}`)
    return response.data
  },

  // Get merchant by user ID
  async getByUserId(userId: string): Promise<Merchant> {
    const response = await merchantApi.get(`/user/${userId}`)
    return response.data
  },

  // Get merchant statistics
  async getStatistics(): Promise<{
    total: number
    active: number
    inactive: number
    byType: Record<string, number>
  }> {
    const response = await merchantApi.get('/statistics')
    return response.data
  },

  // Merchant User Management
  // Get all users for a merchant
  async getMerchantUsers(merchantId: string): Promise<MerchantUserResponse> {
    const response = await merchantApi.get(`/${merchantId}/users`)
    return response.data
  },

  // Add user to merchant
  async addMerchantUser(merchantId: string, userData: AddMerchantUserDto): Promise<{ message: string; merchant_user: MerchantUser }> {
    const response = await merchantApi.post(`/${merchantId}/users`, userData)
    return response.data
  },

  // Update merchant user permission
  async updateMerchantUser(merchantId: string, userId: string, userData: UpdateMerchantUserDto): Promise<{ message: string; merchant_user: MerchantUser }> {
    const response = await merchantApi.put(`/${merchantId}/users/${userId}`, userData)
    return response.data
  },

  // Remove user from merchant
  async removeMerchantUser(merchantId: string, userId: string): Promise<{ message: string; merchant_user: MerchantUser }> {
    const response = await merchantApi.delete(`/${merchantId}/users/${userId}`)
    return response.data
  },

  // ============================================================================
  // ORGANIZATION & REGION MANAGEMENT (for global brand selection)
  // ============================================================================

  // Get all organizations (global brands)
  async getOrganizations(): Promise<OrganizationsResponse> {
    const response = await organizationApi.get('')
    return response.data
  },

  // Get organization by ID
  async getOrganizationById(id: string): Promise<Organization> {
    const response = await organizationApi.get(`/${id}`)
    return response.data
  },

  // Create a new organization
  async createOrganization(data: CreateOrganizationDto): Promise<Organization> {
    const response = await organizationApi.post('', data)
    return response.data
  },

  // Update an organization
  async updateOrganization(id: string, data: UpdateOrganizationDto): Promise<Organization> {
    const response = await organizationApi.put(`/${id}`, data)
    return response.data
  },

  // Delete an organization (soft delete)
  async deleteOrganization(id: string): Promise<{ message: string; organization: Organization }> {
    const response = await organizationApi.delete(`/${id}`)
    return response.data
  },

  // Get regions for a specific organization
  async getOrganizationRegions(organizationId: string): Promise<OrganizationRegionsResponse> {
    const response = await organizationApi.get(`/${organizationId}/regions`)
    return response.data
  },

  // Get region by ID
  async getRegionById(id: string): Promise<OrganizationRegion> {
    const response = await organizationApi.get(`/regions/${id}`)
    return response.data
  },

  // Create a new region for an organization
  async createOrganizationRegion(organizationId: string, data: CreateOrganizationRegionDto): Promise<OrganizationRegion> {
    const response = await organizationApi.post(`/${organizationId}/regions`, data)
    return response.data
  },

  // Update a region
  async updateRegion(id: string, data: UpdateOrganizationRegionDto): Promise<OrganizationRegion> {
    const response = await organizationApi.put(`/regions/${id}`, data)
    return response.data
  },

  // Delete a region
  async deleteRegion(id: string): Promise<{ message: string; region: OrganizationRegion }> {
    const response = await organizationApi.delete(`/regions/${id}`)
    return response.data
  },

  // Global regions methods
  async getAvailableGlobalRegions(filters?: GlobalRegionFilters): Promise<GlobalRegion[]> {
    try {
      const response = await configService.regions.getAll(filters || {})
      return response.data || []
    } catch (error) {
      console.error('Error fetching global regions:', error)
      return []
    }
  },

  async getGlobalRegionById(id: string): Promise<GlobalRegion | null> {
    // Validate ID
    if (!id || id === 'undefined' || id === 'null') {
      console.error('Invalid region ID:', id)
      return null
    }
    
    try {
      return await configService.regions.getById(id)
    } catch (error) {
      console.error('Error fetching global region:', error)
      return null
    }
  },

  async createGlobalRegion(regionData: any): Promise<GlobalRegion | null> {
    try {
      return await configService.regions.create(regionData)
    } catch (error) {
      console.error('Error creating global region:', error)
      return null
    }
  },

  async linkGlobalRegionToOrganization(
    organizationId: string,
    geographicRegionId: string,
    customData?: Partial<CreateOrganizationRegionDto>
  ): Promise<OrganizationRegion> {
    // Validate geographicRegionId
    if (!geographicRegionId || geographicRegionId === 'undefined' || geographicRegionId === 'null') {
      throw new Error('Invalid region ID')
    }

    // First get the global region details
    const globalRegion = await this.getGlobalRegionById(geographicRegionId)
    if (!globalRegion) {
      throw new Error('Global region not found')
    }

    // Create organization region linked to global region
    const regionData: CreateOrganizationRegionDto = {
      name: customData?.name || globalRegion.name,
      code: customData?.code || globalRegion.code,
      description: customData?.description || `Linked to global region: ${globalRegion.name}`,
      geographic_region_id: geographicRegionId,
      is_active: customData?.is_active !== undefined ? customData.is_active : true,
      ...customData
    }

    return await this.createOrganizationRegion(organizationId, regionData)
  },

  // Get organization hierarchy
  async getOrganizationHierarchy(organizationId: string): Promise<{
    organization_id: string;
    hierarchy: any[]
  }> {
    const response = await merchantApi.get(`/hierarchy/${organizationId}`)
    return response.data
  },

  // Get merchants by organization
  async getMerchantsByOrganization(organizationId: string): Promise<{
    organization_id: string;
    merchants: Merchant[]
  }> {
    const response = await merchantApi.get(`/organization/${organizationId}`)
    return response.data
  },

  // ============================================
  // FONCTIONS SUPPLEMENTAIRES POUR GESTION DES ROLES
  // ============================================

  // Récupérer les sous-commerces (enfants) d'un commerce
  async getMerchantChildren(merchantId: string): Promise<{
    parent_merchant_id: string;
    children: Array<{
      merchant_id: string;
      business_name: string;
      business_description: string;
      business_type: string;
      is_active: boolean;
      organization_id?: string;
      org_region_id?: string;
      parent_store_id?: string;
      store_code?: string;
      region_name?: string;
    }>
  }> {
    const response = await merchantApi.get(`/${merchantId}/children`)
    return response.data
  },

  // Récupérer toutes les boutiques d'un utilisateur (nouveau endpoint)
  async getUserMerchants(userId: string): Promise<{
    user_id: string;
    merchants: Array<{
      merchant_user_id: string;
      merchant_id: string;
      business_name: string;
      business_description: string;
      business_type: string;
      merchant_active: boolean;
      permission_level: 'owner' | 'admin' | 'manager' | 'viewer';
      can_manage_users: boolean;
      user_active: boolean;
      created_at: string;
      updated_at: string;
      organization_name?: string;
      region_name?: string;
    }>
  }> {
    const response = await merchantApi.get(`/users/${userId}/merchants`)
    return response.data
  },

  // Vérifier si un utilisateur a accès à une boutique
  async checkMerchantAccess(
    userId: string,
    merchantId: string,
    requiredPermission: 'owner' | 'admin' | 'manager' | 'viewer' = 'viewer'
  ): Promise<boolean> {
    try {
      const userMerchants = await this.getUserMerchants(userId)
      const merchant = userMerchants.merchants.find(m => m.merchant_id === merchantId && m.user_active)
      
      if (!merchant) {
        return false
      }

      // Hiérarchie des permissions
      const permissionHierarchy = {
        'owner': 4,
        'admin': 3,
        'manager': 2,
        'viewer': 1
      }

      const userLevel = permissionHierarchy[merchant.permission_level] || 0
      const requiredLevel = permissionHierarchy[requiredPermission] || 0
      
      return userLevel >= requiredLevel
    } catch (error) {
      console.error('Error checking merchant access:', error)
      return false
    }
  }
}

export default merchantService
